/**
 * Web push notification helpers.
 *
 * Flow:
 *   1. Ask browser for permission via `Notification.requestPermission()`
 *   2. Get the active service worker registration
 *   3. Call `pushManager.subscribe()` with our VAPID public key
 *   4. Send the subscription to /api/notifications/push/subscribe
 *
 * All calls short-circuit gracefully if the browser or server lacks support.
 */
import api from "@/lib/api";

const LS_DISMISS_KEY = "cms_push_prompt_dismissed_at";
const LS_STATE_KEY = "cms_push_state"; // "enabled" | "denied"

// Base64URL → Uint8Array (browser PushSubscription applicationServerKey format)
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

/** Was the "Enable notifications" prompt dismissed recently? */
export function promptDismissedRecently(days = 14) {
  const at = Number(localStorage.getItem(LS_DISMISS_KEY) || 0);
  if (!at) return false;
  return Date.now() - at < days * 24 * 60 * 60 * 1000;
}

export function markPromptDismissed() {
  localStorage.setItem(LS_DISMISS_KEY, String(Date.now()));
}

/** Ask permission + subscribe + POST to backend. Returns { ok, reason }. */
export async function enablePushNotifications() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    if (perm === "denied") localStorage.setItem(LS_STATE_KEY, "denied");
    return { ok: false, reason: perm };
  }
  const { data: cfg } = await api.get("/notifications/push/vapid-key").catch(() => ({ data: null }));
  if (!cfg || !cfg.public_key) return { ok: false, reason: "server_not_configured" };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(cfg.public_key),
  });
  const payload = sub.toJSON();
  const res = await api.post("/notifications/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
    user_agent: navigator.userAgent,
  });
  if (res.data?.ok) {
    localStorage.setItem(LS_STATE_KEY, "enabled");
    return { ok: true, subscription_id: res.data.subscription_id };
  }
  return { ok: false, reason: "backend_error" };
}

export async function disablePushNotifications() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.post("/notifications/push/unsubscribe", { endpoint: sub.endpoint })
      .catch(() => null);
    await sub.unsubscribe();
  }
  localStorage.removeItem(LS_STATE_KEY);
  return { ok: true };
}

export async function sendPushSelfTest() {
  const { data } = await api.post("/notifications/push/test");
  return data;
}

/** True when: browser supports it, permission is default (not asked yet),
 *  and the user hasn't dismissed the prompt in the last 14 days. */
export function shouldShowPushPrompt() {
  if (!pushSupported()) return false;
  if (Notification.permission !== "default") return false;
  if (promptDismissedRecently()) return false;
  return true;
}

/** True when running inside an installed PWA (home-screen or app mode). */
export function isRunningStandalone() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

/** iOS Safari — install to home screen has to be manual (Share menu). */
export function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iP(hone|od|ad)/i.test(ua);
}
